import type { JobStartResult, RangeRequestParams } from "@entrilia/shared/api/types";
import type { Maybe } from "@entrilia/shared/types";
import type { ObjectFieldSort } from "./objectTypes";

export interface WorkbookItem {
  id: string;
  name: string;
  description: string;
  uploadedAt: string;
  categoryIds: string[];
  dataCatalogId: string;
}

export interface UpdateWorkbookRequest {
  title?: string;
  description?: string;
  categoryIds?: string[];
  uploadedAt?: string;
}

export const allWorkbookRunStatuses = ["InProgress", "Completed", "Error", "Canceled", "Skipped", "Queued"] as const;
export type WorkbookRunStatus = (typeof allWorkbookRunStatuses)[number];

export interface ReportsUpdatedProgress {
  completed: number;
  total: number;
  percentage: number;
}

export interface WorkbookRun {
  id: string;
  clientCode: string;
  name: string;
  status: WorkbookRunStatus;
  templateId: string;
  templateName: string;
  isTemplateDeleted?: boolean;
  createdBy: string;
  reportsSettings: WorkbookRunReport[];
  workbookFilters: WorkbookFilter[];
  runnableReportsNumber: number;
  errorMessage?: string;
  operationId?: string;
  processedItems: number;
  canceledBy?: string;
  completedAt?: string;
  canceledAt?: string;
  failedAt?: string;
  createdAt: string; // ISO date string
  catalogueId?: string;
  queuedAt?: string;
  startedAt?: string;
  hasTooManyCustomNumberFormats: boolean;
}

export interface RunWorkbookRequest {
  templateId: string;
  workbookFilters: WorkbookFilter[];
  name: string;
}

export interface SearchWorkbookRunsRequest {
  range: RangeRequestParams;
  searchTerm?: string;
  statuses?: WorkbookRunStatus[];
  templateIds?: string[];
  startedAtFrom?: string;
  startedAtTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  createdBy?: string;
  createdByMe?: boolean;
  sort?: ObjectFieldSort;
}

export interface SearchWorkbookTemplatesRequest {
  range: RangeRequestParams;
  searchTerm?: string;
  createdByMe?: boolean;
  sort?: ObjectFieldSort;
}

export interface RunWorkbookResponse {
  runId: string;
  operationId: Pick<JobStartResult, "operationId">;
}

export type BoundaryType = "From" | "To" | "In";

export interface ReportLinkedFilter {
  id: string;
  bound?: BoundaryType;
}

interface ReportAccessStateReportRequest {
  reportCode: string;
  reportSource: ReportSource;
  clientCode: string;
  reportKind: ReportKind;
}
export interface ReportsAccessStateRequest {
  reports: ReportAccessStateReportRequest[];
}

export interface CancelWorkbookRunsRequest {
  runs: Record<string, string>;
}

export interface DownloadWorkbooksRequest {
  runIds: string[];
}

export interface DeleteWorkbookRunsRequest {
  runIds: string[];
}

export interface DeleteWorkbookTemplatesRequest {
  templateIds: string[];
}

interface CancelJobResult {
  workbookRun: WorkbookRun;
  isCanceled: boolean;
}

export interface CancelJobsResponse {
  results: Record<string, Maybe<CancelJobResult>>;
}

export interface ReportAccessStateResponse {
  data: ReportAccessState[];
}

export interface ReportAccessState {
  storedReportId?: string;
  reportCode: string;
  hasAccessToReport: boolean;
  isReportDeleted: boolean;
  isConfigurationValid: boolean;
  isProcessing: boolean;
  message?: string;
}

type DimensionsData = Record<ReportSource, Record<DimensionName, KeyFieldName>>;

export interface DictionaryData {
  source: ReportSource;
  parameterName: string;
  primaryKeyField: string;
  secondaryKeyField: string;
}

type DimensionName = string;
type KeyFieldName = string;

export interface WorkbookFilterSelectedOption {
  properties: Record<string, ParameterDictionaryValue>;
  subValues?: ParameterDictionaryValue[];
}

export interface WorkbookFilter {
  id: string;
  name: string | null;
  createdBy: string;
  createdAt: number;
  updatedBy?: string;
  updatedAt?: number;
  value: ParameterDictionaryValue[];
  linkedFilters: ReportLinkedFilter[];
  type: ExcelReportParameterType;
  equalityType: FilterModelType;
  clientCode?: string;
  filterIdentifier: string | undefined;
  selectedOptions: WorkbookFilterSelectedOption[] | undefined;
  dimensions?: DimensionsData;
  dictionaryLoadSettings?: DictionaryData;
}

export interface WorkbookTemplate {
  id: string;
  fileName: string;
  name: string;
  ext: string;
  clientCode: string;
  catalogueId: string;
  createdAt: string;
  createdBy: string;
  reportsSettings: WorkbookReport[];
  workbookFilters: WorkbookFilter[];
  hasTooManyCustomNumberFormats: boolean;
}

export enum ReportSource {
  BC = "BC",
  BI = "BI",
}

export enum ReportKind {
  Report = "Report",
  MasterDataReport = "MasterDataReport",
}

export enum FilterModelType {
  Equal = "Equal",
  NotEqual = "NotEqual",
  LessThan = "LessThan",
  LessThanOrEqual = "LessThanOrEqual",
  Between = "Between",
  GreaterThan = "GreaterThan",
  GreaterThanOrEqual = "GreaterThanOrEqual",
  Contains = "Contains",
  DoesntContain = "DoesntContain",
}

// Deliberately incomplete: a date parameter's operator is never shown.
const filterModelTypeLabels: Partial<Record<FilterModelType, string>> = {
  [FilterModelType.Equal]: "is",
  [FilterModelType.NotEqual]: "is not",
  [FilterModelType.Contains]: "contains",
  [FilterModelType.DoesntContain]: "doesn't contain",
};

const dateParameterTypes: ExcelReportParameterType[] = ["Date", "Daterange"];

export const getFilterModelTypeLabel = (
  equalityType: FilterModelType | undefined,
  parameterType?: ExcelReportParameterType
): string | undefined => {
  if (parameterType !== undefined && dateParameterTypes.includes(parameterType)) {
    return undefined;
  }
  return equalityType === undefined ? undefined : filterModelTypeLabels[equalityType];
};

export type ParameterDictionaryValue = string | null;

export type DimensionDictionary = { id: number } & Record<string, ParameterDictionaryValue>;

export type ParameterDictionary = DimensionDictionary & {
  subValues?: ParameterDictionaryValue[];
};

export type ExcelReportParameterType = "Date" | "Daterange" | "Table" | "Boolean" | "Option";

export type WorkbookReportParameter = {
  id: string;
  name: string;
  values: ParameterDictionaryValue[];
  text?: string;
  equalityType?: FilterModelType;
  description?: string;
  reportSource: ReportSource;
  type: ExcelReportParameterType;
  allDictionaryItems?: ParameterDictionary[];
  availableDictionaryItems?: ParameterDictionary[];
  subValuesParameterNo?: string;
  childParameterId?: string;
  keyFieldName: string;
};

export interface WorkbookReport {
  id: string;
  caption: string;
  address: string;
  worksheetId: string;
  worksheetName: string;
  reportCode: string;
  reportSource: ReportSource;
  reportKind: ReportKind;
  filters: WorkbookReportParameter[];
  changedAt?: number;
  clientCode: string;
  reportType: string;
  changedBy?: string;
  withParameters: boolean;
  selectedColumns: number[] | undefined;
  isLocked?: boolean;
  originalWorksheetName?: string;
  additionalSheets?: WorkbookReportAdditionalSheet[];
}

export interface WorkbookReportAdditionalSheet {
  address: string;
  worksheetName: string;
  worksheetId: string;
  originalWorksheetName: string;
}

export interface WorkbookRunReport extends WorkbookReport {
  startedBy?: string;
  startedAt?: string;
  canceledBy?: string;
  canceledAt?: string;
  completedAt?: string;
  status?: WorkbookRunStatus;
  errorMessage?: string;
  hasAccessToReport?: boolean;
  isReportDeleted?: boolean;
  isConfigurationValid?: boolean;
  isProcessing?: boolean;
  failedAt?: string;
}

export interface WorkbookRunsProgress {
  result: WorkbookRunProgressItem[];
}

export interface WorkbookRunProgressItem {
  runId: string;
  processedItems: number;
  currentStatus: WorkbookRunStatus;
  finishedAt?: string;
  startedAt?: string;
}

export interface GetWorkbookRunsRequest {
  page?: number;
  pageSize?: number;
  status?: WorkbookRunStatus;
  search?: string;
  sortBy?: "startedAt" | "status" | "workbookTemplate" | "startedBy" | "duration";
  sortOrder?: "asc" | "desc";
}

export interface GetWorkbookRunsResponse {
  runs: WorkbookRun[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface GetWorkbookRunTemplatesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface GetWorkbookRunTemplatesResponse {
  templates: WorkbookTemplate[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface CreateWorkbookRunRequest {
  workbookTemplateId: string;
  description?: string;
}

export interface CreateWorkbookRunResponse {
  runId: string;
  status: WorkbookRunStatus;
}

export interface UpdateWorkbookRunRequest {
  runId: string;
  status?: WorkbookRunStatus;
  description?: string;
}

export interface DeleteWorkbookRunRequest {
  runId: string;
}

export interface CreateWorkbookRunTemplateRequest {
  name: string;
  description?: string;
}

export interface CreateWorkbookRunTemplateResponse {
  templateId: string;
}

export interface UpdateWorkbookRunTemplateRequest {
  templateId: string;
  name?: string;
  description?: string;
}

export interface DeleteWorkbookRunTemplateRequest {
  templateId: string;
}

export interface GroupField {
  fieldName: string;
  valueCaptions: Record<string, string>;
}

export interface ParameterDictionaryResponse {
  dictionaryItems: {
    items: DimensionDictionary;
    subValues?: string[];
  }[];
  keyFieldName: string;
  groupByField?: GroupField;
}
